/**
 *
 * Map
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { compose } from 'redux';
import { Map as LeafletMap, ScaleControl, ZoomControl } from 'react-leaflet';
import debounce from 'lodash/debounce';
import Control from 'react-leaflet-control';
import withUiControl from 'containers/UiControl/withUiControl';
import withEdit from 'containers/EditService/withEdit';
import GeoSearch from 'containers/GeoSearch';
import ControlPanel from 'components/ControlPanel';
import LayersControl from 'components/LayersControl';
import ClubMarkers from 'components/ClubMarkers';
import CurrentPosition from 'components/CurrentPosition';
import LocationMarkers from 'components/LocationMarkers';
import RouteMarkers from 'components/RouteMarkers';
import RoutePath from 'components/RoutePath';
import InfoControl from 'components/InfoControl';
import EditControl from 'components/EditControl';
import EditRoute from 'components/EditRoute';
import FullscreenControl from 'components/FullscreenControl';
import { IFRAME, EMBEDDED } from 'containers/App/constants';
import {
  PANEL_WIDTH,
  PANEL_WIDTH_LG,
  LG_MIN,
} from 'components/ControlPanel/constants';
import getSnap from 'utils/getSnap';
import minWidth, { minHeight } from 'utils/minWidth';
import animate from 'utils/animate';
import { Track } from 'utils/analytics';
import RoutingControl from 'components/RoutingControl';
import LocationControl from 'components/LocationControl';
import ShowSelected from 'components/SearchControl/ShowSelected';
import AlertList from 'components/AlertList';
import withAlerts from 'containers/AlertService/withAlerts';
import RoutesAlert from 'components/RouteMarkers/RoutesAlert';
import StravaAlert from 'components/StravaAlert';
import { editStatus } from 'containers/EditService/functions/Leg';
import { STATUS_NONE } from 'containers/EditService/constants';
import Promotion from '../Promotion';
import LogoControl from '../LogoControl';
import ElevationChart from '../ElevationChart';
import { ELEVATION_HEIGHT } from './constants';
import ElevationProvider from '../ElevationChart/ElevationProvider';
import ElevationLocation from '../ElevationChart/ElevationLocation';

function getCursor(props) {
  if (EMBEDDED) {
    return 'pointer';
  }
  if (props.editing) {
    return 'crosshair';
  }
  return '';
}

const Wrapper = styled.main`
  position: relative;
  flex-grow: 1;
  display: flex;
  .leaflet-container {
    flex-grow: 1;
    cursor: ${getCursor};
  }
  #main-map.with-elevation {
    margin-bottom: ${ELEVATION_HEIGHT}px;
  }

  @media screen {
    .leaflet-left.leaflet-top {
      left: 20px;
      transition: left 200ms;
    }
    @media (max-width: 359px) {
      .leaflet-touch .leaflet-control-layers-toggle {
        width: 36px;
        height: 36px;
      }
    }
    @media (max-width: 479px) {
      .leaflet-left.leaflet-top {
        z-index: ${p => (p.hasRoute ? 900 : 1000)};
      }
    }
    @media (min-width: 360px) {
      .leaflet-touch .leaflet-bar a {
        width: 40px;
        height: 40px;
        line-height: 40px;
      }
    }
    @media (max-height: 479px) {
      .leaflet-control-scale {
        display: none;
      }
      .leaflet-control-zoom {
        display: flex;
        a {
          border: none;
        }
        a + a {
          border-left: 1px solid #ccc;
        }
      }
      .leaflet-control-edit {
        flex-direction: row;
        .btn + .btn {
          margin-left: 10px;
        }
      }
    }

    &.with-control .leaflet-left {
      left: ${PANEL_WIDTH}px;
      @media (min-width: ${LG_MIN}px) {
        left: ${PANEL_WIDTH_LG}px;
      }
      &.leaflet-top {
        left: ${PANEL_WIDTH + 20}px;
        @media (min-width: ${LG_MIN}px) {
          left: ${PANEL_WIDTH_LG + 20}px;
        }
      }
    }
    .alertlist {
      position: absolute;
      display: flex;
      z-index: 1030;
      &.alertlist-default {
        flex-direction: column-reverse;
        transition: left 200ms;
        left: 10px;
        bottom: 0;
      }
      &.alertlist-success {
        top: 10px;
        right: 70px;
        flex-direction: column;
      }
      &.alertlist-danger {
        transition: left 200ms;
        left: 50%;
        transform: translateX(-50%);
        top: 40%;
        flex-direction: column;
      }
    }
    &.with-control .alertlist {
      &.alertlist-default {
        left: ${PANEL_WIDTH}px;
        @media (min-width: 480px) {
          left: ${PANEL_WIDTH_LG}px;
        }
      }
      &.alertlist-danger {
        left: calc(${PANEL_WIDTH / 2}px + 50%);
      }
    }
    &.with-control #elevation-wrapper {
      padding-left: ${PANEL_WIDTH}px;
      @media (min-width: 480px) {
        padding-left: ${PANEL_WIDTH_LG}px;
      }
    }
  }
  @media print {
    .leaflet-control {
      display: none;
      &.leaflet-control-info,
      &.leaflet-control-scale,
      &.leaflet-control-attribution {
        display: block;
      }
    }
  }
  &:not(.with-info) .leaflet-control-info {
    display: none;
  }
`;

class Map extends React.PureComponent {
  static hasContent(props) {
    if (!props) {
      return false;
    }
    const {
      showLocations,
      showRoutes,
      params: { clubId, routeId },
    } = props;
    return showLocations || showRoutes || clubId || routeId;
  }

  state = {
    dragging: false,
    dragged: false,
    currentPosition: null,
    layerControlJustOpened: false,
  };

  componentDidMount() {
    // Apparently needed to draw custom controls
    setTimeout(this.forceUpdate.bind(this), 0);
    this.layerControlHandle = setInterval(this.isLayerControlOpen, 300);
    this.checkRouteOnContent();
    this.layerControl = document.getElementsByClassName(
      'leaflet-control-layers',
    );
  }

  componentWillUnmount() {
    if (this.layerControlHandle) {
      clearInterval(this.layerControlHandle);
    }
  }

  componentDidUpdate(prevProps) {
    this.checkRouteOnContent(prevProps);
  }

  checkRouteOnContent(prevProps) {
    const {
      workingRoute: { legs },
      clearRoute,
      createRoute,
    } = this.props;
    const hasContent = Map.hasContent(this.props);
    if ((legs && legs.length > 0) || hasContent === Map.hasContent(prevProps)) {
      return;
    }
    if (editStatus(legs) === STATUS_NONE && !hasContent) {
      createRoute();
      return;
    }
    if (legs && hasContent) {
      clearRoute();
    }
  }

  setCurrentPosition = currentPosition => {
    this.setState(prevState => {
      const { dragged } = prevState;
      return {
        currentPosition,
        dragged: dragged && Boolean(currentPosition),
      };
    });
  };

  handleBaselayerchange = ev => {
    const { onChange } = this.props;
    ev.target.setMaxZoom(ev.layer.options.maxZoom || 18);
    this.layerControl[0].classList.remove('leaflet-control-layers-expanded');
    onChange(ev);
  };

  handleOverlayadd(ev) {
    Track.event({
      category: 'map',
      action: 'overlay',
      label: ev.layer.options.id,
    });
  }

  isLayerControlOpen = () => {
    if (!this.layerControl) {
      return false;
    }
    const open = this.layerControl[0].classList.contains(
      'leaflet-control-layers-expanded',
    );
    if (open && this.props.zoom >= 13) {
      if (this.stravaAlertClosing) {
        // Still pending, clear
        clearTimeout(this.stravaAlertClosing);
      } else {
        // New, so set state to open
        this.setState({ layerControlJustOpened: true });
      }
      this.stravaAlertClosing = setTimeout(() => {
        this.setState({ layerControlJustOpened: false });
        delete this.stravaAlertClosing;
      }, 5000);
    }
    return open;
  };

  handleClick = ev => {
    if (EMBEDDED) {
      window.open(`https://runnermaps.nl${window.location.pathname}`);
      return;
    }
    if (ev.originalEvent.target.id !== 'main-map') {
      return;
    }
    if (this.isLayerControlOpen()) {
      return;
    }
    const { addPoint, travelMode, workingRoute } = this.props;
    if (workingRoute.legs === undefined) {
      animate('edit-action');
      return;
    }
    const snap = getSnap(ev.target);
    addPoint({ ...ev.latlng, snap }, travelMode);
  };

  handleDragging = ev => {
    this.setState(prevState => {
      const { currentPosition, dragged } = prevState;
      const justDragged = currentPosition && ev.distance && ev.distance > 100;
      return {
        dragging: ev.type === 'dragstart',
        dragged: dragged || justDragged,
      };
    });
  };

  getClassName() {
    const { params } = this.props;
    const elem = document.getElementById('map-wrapper');
    if (!elem) {
      const classes = [];
      if (params.routeId || params.clubId) {
        classes.push('with-info');
      }
      return classes.join(' ');
    }
    const classes = elem.classList;
    if (params.routeId || params.clubId) {
      classes.add('with-info');
    } else {
      classes.remove('with-info');
    }
    return elem.className;
  }

  render() {
    const {
      onChange,
      zoom,
      center,
      mapType,
      travelMode,
      workingRoute,
      params,
    } = this.props;
    const {
      dragged,
      dragging,
      currentPosition,
      layerControlJustOpened,
    } = this.state;
    const routeId = params.routeId && Number(params.routeId);
    const editing = editStatus(workingRoute.legs);
    return (
      <ElevationProvider>
        <Wrapper
          id="map-wrapper"
          className={this.getClassName()}
          editing={editing !== STATUS_NONE && !dragging}
          hasRoute={workingRoute.legs && workingRoute.legs.length > 0}
        >
          {!IFRAME && <Promotion />}
          {!EMBEDDED && <ControlPanel editing={editing} />}
          <LeafletMap
            id="main-map"
            center={center}
            zoom={zoom}
            zoomControl={false}
            onBaselayerchange={this.handleBaselayerchange}
            onOverlayadd={this.handleOverlayadd}
            onClick={this.handleClick}
            onDragstart={this.handleDragging}
            onDragend={this.handleDragging}
            onZoomend={debounce(onChange, 10)}
            onMoveend={debounce(onChange, 10)}
            touchZoom
          >
            <CurrentPosition center={currentPosition} />
            <LayersControl travelMode={travelMode} current={mapType} />
            <ScaleControl position="bottomright" />
            <ZoomControl position="bottomright" />
            <LogoControl position="bottomleft" mapType={mapType} />
            <Control className="leaflet-control-info" position="topleft">
              <InfoControl {...this.props} />
            </Control>
            <RouteMarkers {...this.props} />
            <LocationMarkers {...this.props} />
            <ClubMarkers {...this.props} />
            <RoutePath {...this.props} />
            {!EMBEDDED && (
              <React.Fragment>
                {(!minWidth() || !minHeight()) && (
                  <Control position="topright">
                    <FullscreenControl />
                  </Control>
                )}
                <Control position="topleft">
                  <GeoSearch />
                </Control>
                <Control position="topleft">
                  <LocationControl
                    onChange={this.setCurrentPosition}
                    dragged={dragged}
                  />
                </Control>
                <ShowSelected />
                <EditRoute {...this.props} />
                <RoutingControl travelMode={travelMode} onChange={onChange} />
                <ElevationLocation />
                <Control position="bottomright">
                  <EditControl
                    travelMode={travelMode}
                    onChange={onChange}
                    routeId={routeId}
                  />
                </Control>
              </React.Fragment>
            )}
            <Control position="bottomleft">
              <RoutesAlert {...this.props} />
              <StravaAlert
                zoom={zoom}
                layerControlOpen={layerControlJustOpened}
              />
            </Control>
            <AlertList />
          </LeafletMap>
          <ElevationChart {...this.props} map={this} />
        </Wrapper>
      </ElevationProvider>
    );
  }
}

Map.propTypes = {
  zoom: PropTypes.number,
  center: PropTypes.object,
  createRoute: PropTypes.func.isRequired,
  clearRoute: PropTypes.func.isRequired,
  mapType: PropTypes.string,
  travelMode: PropTypes.string,
  showLocations: PropTypes.bool,
  showRoutes: PropTypes.bool,
  addPoint: PropTypes.func,
  workingRoute: PropTypes.object,
  params: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  addAlert: PropTypes.func.isRequired,
};

export default compose(
  withAlerts,
  withEdit,
  withUiControl(['showLocations', 'showRoutes']),
)(Map);
